import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Loader2, X, Sparkles } from 'lucide-react';
import RichTextEditor from '../RichTextEditor';
import { AttachmentList } from './AttachmentList';
import { flattenTree } from './categoryUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BlockEditorDialog = ({ open, onOpenChange, editing, categories, onSaved }) => {
  const [form, setForm] = useState({ title: '', body: '', category_ids: [], keywords: [], attachments: [] });
  const [kwInput, setKwInput] = useState('');
  const [saving, setSaving] = useState(false);
  const tree = flattenTree(categories);

  useEffect(() => {
    if (!open) return;
    setKwInput('');
    if (editing) {
      setForm({
        title: editing.title || '',
        body: editing.body || '',
        category_ids: editing.category_ids || [],
        keywords: editing.keywords || [],
        attachments: editing.attachments || [],
      });
    } else {
      setForm({ title: '', body: '', category_ids: [], keywords: [], attachments: [] });
    }
  }, [open, editing]);

  const toggleCat = (id) => setForm(f => ({
    ...f,
    category_ids: f.category_ids.includes(id) ? f.category_ids.filter(x => x !== id) : [...f.category_ids, id],
  }));

  const addKeyword = () => {
    const k = kwInput.trim().toLowerCase();
    if (!k) return;
    setForm(f => f.keywords.includes(k) ? f : { ...f, keywords: [...f.keywords, k] });
    setKwInput('');
  };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error('Title and body are required'); return; }
    if (form.category_ids.length === 0) { toast.error('Pick at least one category'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/rfp/boilerplate/${editing.id}`, form, { withCredentials: true });
        toast.success(`"${form.title}" updated`);
      } else {
        await axios.post(`${API_URL}/api/rfp/boilerplate`, form, { withCredentials: true });
        toast.success(`"${form.title}" added — keywords are generating`);
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save the block');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="rfp-block-editor">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${editing.capability_id || 'Block'}` : 'New Capability Block'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="Capability title" data-testid="rfp-block-title-input" />

          <div>
            <p className="text-xs font-medium text-[#0F2040] mb-1.5">
              Categories <span className="text-slate-400 font-normal">— pick one or more; the first pick names the capability ID prefix for new blocks</span>
            </p>
            <div className="border border-slate-200 rounded-md p-2 max-h-36 overflow-y-auto grid grid-cols-2 gap-x-3" data-testid="rfp-block-category-picker">
              {tree.map(c => (
                <label key={c.id} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer hover:bg-slate-50 rounded px-1 py-1"
                  style={{ paddingLeft: `${4 + c.depth * 14}px` }}>
                  <input type="checkbox" className="accent-[#0F2040]" checked={form.category_ids.includes(c.id)}
                    onChange={() => toggleCat(c.id)} data-testid={`rfp-block-cat-check-${c.id}`} />
                  <span className="truncate">{c.name}</span>
                  <span className="text-[9px] font-mono text-slate-400">{c.shortname}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-[#0F2040] mb-1.5">Body</p>
            <RichTextEditor value={form.body} onChange={v => setForm(f => ({ ...f, body: v }))}
              placeholder="Capability content — headings, bold, lists all carry through to the RFP Support Pack Word doc"
              rows={9} data-testid="rfp-block-body-input" />
          </div>

          <div>
            <p className="text-xs font-medium text-[#0F2040] mb-1.5">
              Attachments <span className="text-slate-400 font-normal">— images and documents carry into built RFP responses and the Word export</span>
            </p>
            <AttachmentList attachments={form.attachments}
              onChange={next => setForm(f => ({ ...f, attachments: next }))}
              testId="rfp-block-attachments" />
          </div>

          <div>
            <p className="text-xs font-medium text-[#0F2040] mb-1.5 flex items-center gap-1.5">
              Keywords
              <span className="text-slate-400 font-normal flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-[#4ABFCF]" />
                auto-generated by AI when the body changes — hand-edits below are kept
              </span>
            </p>
            <div className="flex flex-wrap gap-1.5 items-center" data-testid="rfp-block-kw-editor">
              {form.keywords.map(k => (
                <span key={k} className="flex items-center gap-1 text-[10px] bg-slate-100 text-slate-700 border border-slate-200 rounded-full px-2 py-0.5">
                  {k}
                  <button onClick={() => setForm(f => ({ ...f, keywords: f.keywords.filter(x => x !== k) }))}
                    className="text-slate-400 hover:text-red-600" data-testid={`rfp-block-kw-remove-${k}`}>
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
              <input value={kwInput} onChange={e => setKwInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }}
                onBlur={addKeyword} placeholder="+ add keyword"
                className="text-[10px] border border-dashed border-slate-300 rounded-full px-2 py-0.5 w-28 focus:outline-none focus:border-[#4ABFCF]"
                data-testid="rfp-block-kw-input" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving} className="bg-[#0F2040] hover:bg-[#1A3055] text-white"
            data-testid="rfp-block-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
            {editing ? 'Save Changes' : 'Add Capability'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
